import * as THREE from 'three/webgpu';
import type { FeatureFace } from '../../mesh/feature';
import { queryClosestPointOnFeatureFaceMeshViaBvh } from '../../mesh/feature/acceleration/featureFaceSpatialQuery';


/**
 * 曲面投影候选结果。
 */
export interface FeatureSurfaceProjectionCandidate {
  /** 曲面上的最近点。 */
  point: THREE.Vector3;
  /** 最近点处的局部法向。 */
  normal: THREE.Vector3;
  /** 与输入点的平方距离。 */
  distanceSq: number;
}


/**
 * 曲面投影可选参数。
 */
export interface FeatureSurfaceProjectionOptions {
  /** 最大搜索距离，供 BVH 剪枝。 */
  maxThreshold?: number;
}

/**
 * 基于曲面三角网格求输入点的最近点及局部法向。
 *
 * 设计说明：
 * 1. 主线程当前没有 Puppy CAD 内核 原生曲面最近点求值器，因此统一以结果体面代理的三角化网格为真相；
 * 2. 通过 `FeatureFace` 代理几何上的 indirect BVH 求最近点，替代线性遍历全部三角形；
 * 3. 该工具同时服务于基准面、基准点、测量等所有“投影到曲面”链路，禁止调用方各自复制实现。
 *
 * @param entity 曲面面代理。
 * @param targetPoint 输入点（世界坐标）。
 * @param options 可选 BVH 剪枝参数。
 * @returns 投影候选；无法求解时返回 `null`。
 */
export function projectPointOnFeatureSurface(
  entity: FeatureFace,
  targetPoint: THREE.Vector3,
  options?: FeatureSurfaceProjectionOptions,
): FeatureSurfaceProjectionCandidate | null {
  const projection = queryClosestPointOnFeatureFaceMeshViaBvh(entity, targetPoint, options);
  if (!projection) {
    return null;
  }

  return {
    point: projection.point,
    normal: projection.normal,
    distanceSq: projection.distanceSq,
  };
}